/**
 * FrameworkRecommendation Component
 *
 * Analyzes currently enabled compliance frameworks and recommends complementary
 * frameworks to improve security coverage. Provides intelligent suggestions based
 * on what the user already has enabled.
 *
 * Coverage Model:
 * - Each framework focuses on specific security areas
 * - Frameworks complement each other (e.g., EIDSCA + MCSB for identity)
 * - Recommendations prioritize coverage gaps
 *
 * @example
 * ```tsx
 * <FrameworkRecommendation
 *   enabledFrameworks={[{ code: 'MCSB', name: 'Microsoft Cloud Security Benchmark', ... }]}
 *   allFrameworks={[{ code: 'EIDSCA', name: 'EIDSCA', description: '...' }]}
 *   onEnableFramework={(code) => navigateToBpaTab()}
 * />
 * ```
 */

import { useMemo } from 'react'
import {
  LightBulbIcon,
  PlusCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline'
import type { FrameworkScoreDisplay } from '@/types'

interface FrameworkInfo {
  code: string
  name: string
  description: string
}

interface Props {
  /** Currently enabled frameworks with their scores */
  enabledFrameworks: FrameworkScoreDisplay[]
  /** All available frameworks (enabled or not) */
  allFrameworks: FrameworkInfo[]
  /** Callback when user clicks to enable a recommended framework */
  onEnableFramework?: (code: string) => void
}

/**
 * Framework coverage definitions.
 *
 * Each framework focuses on specific security areas and complements other frameworks.
 * This mapping drives the recommendation logic.
 */
const FRAMEWORK_COVERAGE: Record<
  string,
  {
    focuses: string[]
    complements: string[]
    priority: number // Lower = more fundamental
  }
> = {
  MCSB: {
    focuses: ['Identity', 'Data Protection', 'Network Security', 'Asset Management'],
    complements: ['EIDSCA', 'SCUBA', 'MAESTER'],
    priority: 1,
  },
  EIDSCA: {
    focuses: ['Identity', 'Entra ID', 'Conditional Access', 'Authentication'],
    complements: ['MCSB', 'MAESTER'],
    priority: 2,
  },
  SCUBA: {
    focuses: ['Email Security', 'Teams Security', 'SharePoint', 'CISA Compliance'],
    complements: ['MCSB', 'NIST_800_53'],
    priority: 2,
  },
  MAESTER: {
    focuses: ['Security Tests', 'Operational Hygiene', 'Best Practices'],
    complements: ['EIDSCA', 'MCSB'],
    priority: 3,
  },
  NIST_800_53: {
    focuses: ['Compliance', 'Governance', 'Risk Management', 'Federal Requirements'],
    complements: ['MCSB', 'SCUBA'],
    priority: 3,
  },
  NIST_CSF: {
    focuses: ['Cybersecurity Framework', 'Risk Assessment', 'Governance'],
    complements: ['NIST_800_53', 'MCSB'],
    priority: 4,
  },
}

interface Recommendation {
  code: string
  name: string
  description: string
  reason: string
  priority: 'high' | 'medium' | 'low'
}

/**
 * FrameworkRecommendation - Suggests frameworks to improve coverage.
 *
 * Logic:
 * 1. Identifies which frameworks are already enabled
 * 2. Checks which frameworks would complement the enabled ones
 * 3. Prioritizes recommendations based on coverage gaps
 * 4. Returns null if user has comprehensive coverage
 */
export default function FrameworkRecommendation({
  enabledFrameworks,
  allFrameworks,
  onEnableFramework,
}: Props) {
  const recommendations = useMemo((): Recommendation[] => {
    // Get codes of enabled frameworks (case-insensitive)
    const enabledCodes = new Set(
      enabledFrameworks.map((f) => f.code.toUpperCase())
    )

    const recs: Recommendation[] = []

    // Iterate through all available frameworks
    for (const framework of allFrameworks) {
      const code = framework.code.toUpperCase()

      // Skip if already enabled
      if (enabledCodes.has(code)) continue

      const coverage = FRAMEWORK_COVERAGE[code]
      if (!coverage) continue

      // Check if this framework complements any enabled framework
      const complementsEnabled = coverage.complements.some((c) =>
        enabledCodes.has(c.toUpperCase())
      )

      if (complementsEnabled) {
        // Find which enabled frameworks this one complements
        const complementedFrameworks = coverage.complements
          .filter((c) => enabledCodes.has(c.toUpperCase()))
          .join(', ')

        recs.push({
          code: framework.code,
          name: framework.name,
          description: framework.description,
          reason: `Complements your ${complementedFrameworks} coverage`,
          priority: 'high',
        })
      } else if (enabledCodes.size < 2) {
        // If user has few frameworks, suggest based on focus areas
        const focusAreas = coverage.focuses.slice(0, 2).join(' and ')
        recs.push({
          code: framework.code,
          name: framework.name,
          description: framework.description,
          reason: `Adds ${focusAreas} coverage`,
          priority: 'medium',
        })
      }
    }

    // Sort by priority (high first), then by framework priority
    const priorityOrder = { high: 0, medium: 1, low: 2 }
    recs.sort((a, b) => {
      const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority]
      if (priorityDiff !== 0) return priorityDiff

      // Secondary sort by framework's fundamental priority
      const aFrameworkPriority = FRAMEWORK_COVERAGE[a.code.toUpperCase()]?.priority ?? 99
      const bFrameworkPriority = FRAMEWORK_COVERAGE[b.code.toUpperCase()]?.priority ?? 99
      return aFrameworkPriority - bFrameworkPriority
    })

    // Return top 2 recommendations
    return recs.slice(0, 2)
  }, [enabledFrameworks, allFrameworks])

  // Do not render if no recommendations
  if (recommendations.length === 0) {
    return null
  }

  return (
    <div className="bg-gradient-to-r from-amber-500/5 to-transparent border border-amber-500/20 rounded-2xl p-6">
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="p-2.5 rounded-xl bg-amber-500/15 flex-shrink-0">
          <LightBulbIcon className="h-6 w-6 text-amber-400" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-slate-100">
            Improve Your Coverage
          </h3>
          <p className="mt-1 text-sm text-slate-400">
            Based on your current frameworks, consider enabling:
          </p>

          {/* Recommendations */}
          <div className="mt-4 space-y-3">
            {recommendations.map((rec) => (
              <div
                key={rec.code}
                className="flex items-center justify-between p-3 rounded-xl bg-dark-800/50 border border-dark-700"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <ShieldCheckIcon className="h-5 w-5 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="font-medium text-slate-200 truncate">
                      {rec.name}
                    </div>
                    <div className="text-xs text-slate-500 truncate">
                      {rec.reason}
                    </div>
                  </div>
                </div>

                {onEnableFramework && (
                  <button
                    onClick={() => onEnableFramework(rec.code)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-amber-500/10 text-amber-400 border border-amber-500/30 hover:bg-amber-500/20 transition-colors flex-shrink-0 ml-3"
                  >
                    <PlusCircleIcon className="h-4 w-4" />
                    Enable
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Tip for comprehensive coverage */}
          {enabledFrameworks.length >= 3 && (
            <p className="mt-4 text-xs text-slate-500">
              You have good framework coverage. These suggestions would further strengthen specific areas.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
